import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, TextField, MenuItem, Button, Divider, Card, CardContent, InputLabel, FormControl, Select, Container, FormControlLabel, Checkbox, CircularProgress
} from '@mui/material';
import { ArrowForward, ArrowBack, Save as SaveIcon } from '@mui/icons-material';

const defaultState = {
    attentionSitting: {
        sustainsAttentionBefore: "Never", sustainsAttentionAfter: "Rarely",
        sittingToleranceBefore: "", sittingToleranceAfter: "",
        easilyDistractedBefore: "Always", easilyDistractedAfter: "Sometimes",
        comment: ""
    },
    compliance: {
        simpleInstructionsBefore: "Never", simpleInstructionsAfter: "Sometimes",
        twoStepInstructionsBefore: "Never", twoStepInstructionsAfter: "Rarely",
        needsPromptsBefore: "Always", needsPromptsAfter: "Sometimes",
        comment: ""
    },
    emotionalRegulation: {
        tantrumsBefore: "Frequent", tantrumsAfter: "Occasional",
        cryingSpellsBefore: "Frequent", cryingSpellsAfter: "Rare",
        difficultyCalmingBefore: "Frequent", difficultyCalmingAfter: "Occasional",
        comment: ""
    },
    aggressiveBehavior: {
        hittingKickingBefore: "Not Seen", hittingKickingAfter: "Not Seen",
        bitingBefore: "Not Seen", bitingAfter: "Not Seen",
        headBangingBefore: "Not Seen", headBangingAfter: "Not Seen",
        selfHarmBefore: "Not Seen", selfHarmAfter: "Not Seen",
        selfHarmSpecify: "",
        comment: ""
    },
    repetitiveBehavior: {
        handFlappingBefore: "Frequent", handFlappingAfter: "Occasional",
        verbalStemmingBefore: "Frequent", verbalStemmingAfter: "Occasional",
        rockingSpinningBefore: "Frequent", rockingSpinningAfter: "Rare",
        liningUpBefore: "Frequent", liningUpAfter: "Occasional",
        comment: ""
    },
    sensoryRelated: {
        soundBefore: "Over-responsive", soundAfter: "Over-responsive",
        touchBefore: "Typical", touchAfter: "Typical",
        lightBefore: "Typical", lightAfter: "Typical",
        smellTasteBefore: "Typical", smellTasteAfter: "Typical",
        comment: ""
    },
    socialBehavior: {
        eyeContactBefore: "Never", eyeContactAfter: "Sometimes",
        respondsToNameBefore: "Never", respondsToNameAfter: "Sometimes",
        engagesWithPeersBefore: "Never", engagesWithPeersAfter: "Rarely",
        seeksAdultHelpBefore: "Never", seeksAdultHelpAfter: "Sometimes",
        comment: ""
    },
    transitionsFlexibility: {
        difficultyWithTransitionsBefore: "Always", difficultyWithTransitionsAfter: "Sometimes",
        upsetWithRoutineBefore: "Always", upsetWithRoutineAfter: "Sometimes",
        comment: ""
    },
    triggers: {
        deniedAccess: false, transition: false, sensoryOverload: false,
        demandPlaced: false, peerInteraction: false, unknown: false
    },
    managementStrategies: {
        verbalPraise: "", rewards: "", physicalPrompts: "",
        visualSupports: "", constantSupervision: ""
    },
    tactSkills: {
        objectLabeling: {
            commonObjectBefore: "", commonObjectAfter: "",
            personalItemBefore: "", personalItemAfter: "",
            classroomItemBefore: "", classroomItemAfter: "",
            householdItemBefore: "", householdItemAfter: "",
            toysBefore: "", toysAfter: ""
        },
        actionLabeling: {
            runningBefore: "", runningAfter: "",
            eatingBefore: "", eatingAfter: "",
            sleepingBefore: "", sleepingAfter: "",
            jumpingBefore: "", jumpingAfter: "",
            playingBefore: "", playingAfter: "",
            sittingBefore: "", sittingAfter: ""
        },
        categoryLabeling: {
            animalsBefore: "", animalsAfter: "",
            fruitsBefore: "", fruitsAfter: "",
            vegetablesBefore: "", vegetablesAfter: "",
            goodsBefore: "", goodsAfter: "",
            occupationBefore: "", occupationAfter: "",
            bodyPartsBefore: "", bodyPartsAfter: "",
            clothesBefore: "", clothesAfter: "",
            genderBefore: "", genderAfter: "",
            vehicleBefore: "", vehicleAfter: ""
        },
        colorLabeling: {
            redBefore: "", redAfter: "",
            blueBefore: "", blueAfter: "",
            greenBefore: "", greenAfter: "",
            yellowBefore: "", yellowAfter: "",
            otherColoursBefore: "", otherColoursAfter: ""
        },
        alphabetNumberLabeling: {
            alphabetBefore: "", alphabetAfter: "",
            numberBefore: "", numberAfter: "",
            teluguBefore: "", teluguAfter: "",
            shapesBefore: "", shapesAfter: ""
        },
        comment: ""
    },
    imitationSkills: {
        motorImitation: "Inconsistent",
        verbalImitation: "Inconsistent",
        comment: ""
    },
    playSocialSkills: {
        functionalPlay: "Not observed",
        parallelPlay: "Not observed",
        pretendPlay: "Not observed",
        jointAttention: "Not observed",
        comment: ""
    },
    communicationSkills: {
        comprehensionBefore: "Absent", comprehensionAfter: "Absent",
        expressionBefore: "Absent", expressionAfter: "Absent",
        comment: ""
    },
    summaries: {
        strengths: "",
        needs: "",
        overallSummary: ""
    }
};

const frequencyOptions = ["Always", "Sometimes", "Rarely", "Never"];
const behavioralFrequencyOptions = ["Frequent", "Occasional", "Rare", "Not Seen"];
const sensoryOptions = ["Over-responsive", "Under-responsive", "Typical(Normal)"];
const imitationOptions = ["Consistent", "Inconsistent", "Prompt based"];
const playOptions = ["Present", "Observed", "Not observed", "Emerging with person"];
const presentAbsentOptions = ["Present", "Absent"];

const DICT = {
    "Attention & Sitting Tolerance": { te: "శ్రద్ధ & కూర్చునే సామర్థ్యం" },
    "Compliance & Instructions": { te: "విధేయత & సూచనలు" },
    "Emotional Regulation": { te: "భావోద్వేగ నియంత్రణ" },
    "Aggressive Behavior": { te: "దురహంకార ప్రవర్తన" },
    "Repetitive Behaviours": { te: "పునరావృత ప్రవర్తనలు" },
    "Sensory Area": { te: "జ్ఞానేంద్రియ ప్రాంతం" },
    "Social Behaviour": { te: "సామాజిక ప్రవర్తన" },
    "Transitions & Flexibility": { te: "పరివర్తనాలు & సరళత" },
    "Behaviour Triggers": { te: "ప్రవర్తన ప్రేరేపకాలు" },
    "Management Strategies": { te: "నిర్వహణ వ్యూహాలు" },
    "Tact Skills (Labeling)": { te: "టాక్ట్ నైపుణ్యాలు (లేబులింగ్)" },
    "Imitation Skills": { te: "అనుకరణ నైపుణ్యాలు" },
    "Play & Social Skills": { te: "ఆట & సామాజిక నైపుణ్యాలు" },
    "Communication": { te: "కమ్యూనికేషన్" },
    "Summaries": { te: "సారాంశాలు" }
};

const FormSectionCard = ({ title, children, icon: Icon }) => (
    <Card sx={{ mb: 3, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            {Icon && <Icon fontSize="small" />}
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{title}</Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
            {children}
        </CardContent>
    </Card>
);

const BehavioralReassessmentForm = ({ onSubmit, submitting }) => {
    const [step, setStep] = useState(0);
    const [lang, setLang] = useState('en');
    const [data, setData] = useState(defaultState);

    const lx = (text) => {
        if (lang === 'en') return text;
        return DICT[text] ? (DICT[text].te || text) : text;
    };

    const handleChange = (section, field, value, subSection = null) => {
        if (subSection) {
            setData({
                ...data,
                [section]: {
                    ...data[section],
                    [subSection]: { ...data[section][subSection], [field]: value }
                }
            });
        } else if (section) {
            setData({ ...data, [section]: { ...data[section], [field]: value } });
        } else {
            setData({ ...data, [field]: value });
        }
    };

    const renderBeforeAfterRow = (label, section, beforeField, afterField, options = frequencyOptions, subSection = null) => {
        const currentSection = subSection ? data[section][subSection] : data[section];
        return (
            <Box sx={{ mb: 2, p: 2, borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>{lx(label)}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <InputLabel sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>BEFORE</InputLabel>
                        <Select
                            fullWidth size="small"
                            value={currentSection[beforeField]}
                            onChange={(e) => handleChange(section, beforeField, e.target.value, subSection)}
                            sx={{ borderRadius: '8px', bgcolor: 'white' }}
                        >
                            {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={6}>
                        <InputLabel sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold', color: 'success.main' }}>AFTER</InputLabel>
                        <Select
                            fullWidth size="small"
                            value={currentSection[afterField]}
                            onChange={(e) => handleChange(section, afterField, e.target.value, subSection)}
                            sx={{ borderRadius: '8px', bgcolor: 'white' }}
                        >
                            {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderInput = (label, section, field, options = null, type = "text") => {
        const val = section ? data[section][field] : data[field];
        return (
            <Box sx={{ mb: 2 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>{lx(label)}</InputLabel>
                {options ? (
                    <Select fullWidth size="small" value={val} onChange={(e) => handleChange(section, field, e.target.value)} sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                ) : (
                    <TextField
                        fullWidth size="small" multiline={type === "textarea"} rows={type === "textarea" ? 3 : 1}
                        value={val} onChange={(e) => handleChange(section, field, e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
                    />
                )}
            </Box>
        );
    };

    const steps = [
        {
            title: "Behavioral Basics",
            content: (
                <>
                    <FormSectionCard title={lx("Attention & Sitting Tolerance")}>
                        {renderBeforeAfterRow("Sustains attention to task", "attentionSitting", "sustainsAttentionBefore", "sustainsAttentionAfter")}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>{renderInput("Sitting tolerance (minutes): Before", "attentionSitting", "sittingToleranceBefore")}</Grid>
                            <Grid item xs={6}>{renderInput("After", "attentionSitting", "sittingToleranceAfter")}</Grid>
                        </Grid>
                        {renderBeforeAfterRow("Easily distracted", "attentionSitting", "easilyDistractedBefore", "easilyDistractedAfter")}
                        {renderInput("Comments", "attentionSitting", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Compliance & Instructions")}>
                        {renderBeforeAfterRow("Follows simple instructions", "compliance", "simpleInstructionsBefore", "simpleInstructionsAfter")}
                        {renderBeforeAfterRow("Follows two-step instructions", "compliance", "twoStepInstructionsBefore", "twoStepInstructionsAfter")}
                        {renderBeforeAfterRow("Needs prompts", "compliance", "needsPromptsBefore", "needsPromptsAfter")}
                        {renderInput("Comments", "compliance", "comment", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Emotional & Aggressive",
            content: (
                <>
                    <FormSectionCard title={lx("Emotional Regulation")}>
                        {renderBeforeAfterRow("Tantrums", "emotionalRegulation", "tantrumsBefore", "tantrumsAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Crying spells", "emotionalRegulation", "cryingSpellsBefore", "cryingSpellsAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Difficulty calming self", "emotionalRegulation", "difficultyCalmingBefore", "difficultyCalmingAfter", behavioralFrequencyOptions)}
                        {renderInput("Comments", "emotionalRegulation", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Aggressive Behavior")}>
                        {renderBeforeAfterRow("Hitting / Kicking", "aggressiveBehavior", "hittingKickingBefore", "hittingKickingAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Biting", "aggressiveBehavior", "bitingBefore", "bitingAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Head banging", "aggressiveBehavior", "headBangingBefore", "headBangingAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Self-harm", "aggressiveBehavior", "selfHarmBefore", "selfHarmAfter", behavioralFrequencyOptions)}
                        {renderInput("Self-harm Specify", "aggressiveBehavior", "selfHarmSpecify")}
                        {renderInput("Comments", "aggressiveBehavior", "comment", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Sensory & Repetitive",
            content: (
                <>
                    <FormSectionCard title={lx("Repetitive Behaviours")}>
                        {renderBeforeAfterRow("Hand flapping", "repetitiveBehavior", "handFlappingBefore", "handFlappingAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Verbal stemming", "repetitiveBehavior", "verbalStemmingBefore", "verbalStemmingAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Rocking / Spinning", "repetitiveBehavior", "rockingSpinningBefore", "rockingSpinningAfter", behavioralFrequencyOptions)}
                        {renderBeforeAfterRow("Lining up objects", "repetitiveBehavior", "liningUpBefore", "liningUpAfter", behavioralFrequencyOptions)}
                        {renderInput("Comments", "repetitiveBehavior", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Sensory Area")}>
                        {renderBeforeAfterRow("Sound", "sensoryRelated", "soundBefore", "soundAfter", sensoryOptions)}
                        {renderBeforeAfterRow("Touch", "sensoryRelated", "touchBefore", "touchAfter", sensoryOptions)}
                        {renderBeforeAfterRow("Light", "sensoryRelated", "lightBefore", "lightAfter", sensoryOptions)}
                        {renderBeforeAfterRow("Smell / Taste", "sensoryRelated", "smellTasteBefore", "smellTasteAfter", sensoryOptions)}
                        {renderInput("Comments", "sensoryRelated", "comment", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Social & Transitions",
            content: (
                <>
                    <FormSectionCard title={lx("Social Behaviour")}>
                        {renderBeforeAfterRow("Eye contact", "socialBehavior", "eyeContactBefore", "eyeContactAfter")}
                        {renderBeforeAfterRow("Responds to name", "socialBehavior", "respondsToNameBefore", "respondsToNameAfter")}
                        {renderBeforeAfterRow("Engages with peers", "socialBehavior", "engagesWithPeersBefore", "engagesWithPeersAfter")}
                        {renderBeforeAfterRow("Seeks adult help", "socialBehavior", "seeksAdultHelpBefore", "seeksAdultHelpAfter")}
                        {renderInput("Comments", "socialBehavior", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Transitions & Flexibility")}>
                        {renderBeforeAfterRow("Difficulty with transitions", "transitionsFlexibility", "difficultyWithTransitionsBefore", "difficultyWithTransitionsAfter")}
                        {renderBeforeAfterRow("Upset with routine changes", "transitionsFlexibility", "upsetWithRoutineBefore", "upsetWithRoutineAfter")}
                        {renderInput("Comments", "transitionsFlexibility", "comment", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Triggers & Management",
            content: (
                <>
                    <FormSectionCard title={lx("Behaviour Triggers")}>
                        <Grid container spacing={2}>
                            {["deniedAccess", "transition", "sensoryOverload", "demandPlaced", "peerInteraction", "unknown"].map(t => (
                                <Grid item xs={6} key={t}>
                                    <FormControlLabel
                                        control={<Checkbox checked={data.triggers[t]} onChange={(e) => handleChange("triggers", t, e.target.checked)} />}
                                        label={t.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title={lx("Management Strategies")}>
                        <Grid container spacing={2}>
                            {["verbalPraise", "rewards", "physicalPrompts", "visualSupports", "constantSupervision"].map(s => (
                                <Grid item xs={12} key={s}>
                                    {renderInput(s.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()), "managementStrategies", s)}
                                </Grid>
                            ))}
                        </Grid>
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Tact Skills (Labeling)",
            content: (
                <FormSectionCard title={lx("Tact Skills (Labeling)")}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>A. Object Labeling</Typography>
                    {renderBeforeAfterRow("Common Object", "tactSkills", "commonObjectBefore", "commonObjectAfter", frequencyOptions, "objectLabeling")}
                    {renderBeforeAfterRow("Personal item", "tactSkills", "personalItemBefore", "personalItemAfter", frequencyOptions, "objectLabeling")}
                    {renderBeforeAfterRow("Classroom item", "tactSkills", "classroomItemBefore", "classroomItemAfter", frequencyOptions, "objectLabeling")}
                    {renderBeforeAfterRow("Household item", "tactSkills", "householdItemBefore", "householdItemAfter", frequencyOptions, "objectLabeling")}
                    {renderBeforeAfterRow("Toys", "tactSkills", "toysBefore", "toysAfter", frequencyOptions, "objectLabeling")}
                    
                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>B. Action/Verb Labeling</Typography>
                    {renderBeforeAfterRow("Running", "tactSkills", "runningBefore", "runningAfter", frequencyOptions, "actionLabeling")}
                    {renderBeforeAfterRow("Eating", "tactSkills", "eatingBefore", "eatingAfter", frequencyOptions, "actionLabeling")}
                    {renderBeforeAfterRow("Sleeping", "tactSkills", "sleepingBefore", "sleepingAfter", frequencyOptions, "actionLabeling")}
                    {renderBeforeAfterRow("Jumping", "tactSkills", "jumpingBefore", "jumpingAfter", frequencyOptions, "actionLabeling")}
                    {renderBeforeAfterRow("Playing", "tactSkills", "playingBefore", "playingAfter", frequencyOptions, "actionLabeling")}
                    {renderBeforeAfterRow("Sitting", "tactSkills", "sittingBefore", "sittingAfter", frequencyOptions, "actionLabeling")}

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>C. Category Labeling</Typography>
                    {renderBeforeAfterRow("Animals", "tactSkills", "animalsBefore", "animalsAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Fruits", "tactSkills", "fruitsBefore", "fruitsAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Vegetables", "tactSkills", "vegetablesBefore", "vegetablesAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Goods", "tactSkills", "goodsBefore", "goodsAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Occupation", "tactSkills", "occupationBefore", "occupationAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Body parts", "tactSkills", "bodyPartsBefore", "bodyPartsAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Clothes", "tactSkills", "clothesBefore", "clothesAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Gender", "tactSkills", "genderBefore", "genderAfter", frequencyOptions, "categoryLabeling")}
                    {renderBeforeAfterRow("Vehicle", "tactSkills", "vehicleBefore", "vehicleAfter", frequencyOptions, "categoryLabeling")}

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>D. Colour Labeling</Typography>
                    {renderBeforeAfterRow("Red", "tactSkills", "redBefore", "redAfter", frequencyOptions, "colorLabeling")}
                    {renderBeforeAfterRow("Blue", "tactSkills", "blueBefore", "blueAfter", frequencyOptions, "colorLabeling")}
                    {renderBeforeAfterRow("Green", "tactSkills", "greenBefore", "greenAfter", frequencyOptions, "colorLabeling")}
                    {renderBeforeAfterRow("Yellow", "tactSkills", "yellowBefore", "yellowAfter", frequencyOptions, "colorLabeling")}
                    {renderBeforeAfterRow("Other colours", "tactSkills", "otherColoursBefore", "otherColoursAfter", frequencyOptions, "colorLabeling")}

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>E. Alphabet & Number</Typography>
                    {renderBeforeAfterRow("Alphabet (A-Z)", "tactSkills", "alphabetBefore", "alphabetAfter", frequencyOptions, "alphabetNumberLabeling")}
                    {renderBeforeAfterRow("Number (1-10)", "tactSkills", "numberBefore", "numberAfter", frequencyOptions, "alphabetNumberLabeling")}
                    {renderBeforeAfterRow("Telugu alphabet", "tactSkills", "teluguBefore", "teluguAfter", frequencyOptions, "alphabetNumberLabeling")}
                    {renderBeforeAfterRow("Shapes", "tactSkills", "shapesBefore", "shapesAfter", frequencyOptions, "alphabetNumberLabeling")}
                    {renderInput("Comments", "tactSkills", "comment", null, "textarea")}
                </FormSectionCard>
            )
        },
        {
            title: "Other Skills",
            content: (
                <>
                    <FormSectionCard title={lx("Imitation Skills")}>
                        {renderInput("Motor imitation", "imitationSkills", "motorImitation", imitationOptions)}
                        {renderInput("Verbal imitation", "imitationSkills", "verbalImitation", imitationOptions)}
                        {renderInput("Comments", "imitationSkills", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Play & Social Skills")}>
                        {renderInput("Functional play", "playSocialSkills", "functionalPlay", playOptions)}
                        {renderInput("Parallel play", "playSocialSkills", "parallelPlay", playOptions)}
                        {renderInput("Pretend play", "playSocialSkills", "pretendPlay", playOptions)}
                        {renderInput("Joint attention", "playSocialSkills", "jointAttention", playOptions)}
                        {renderInput("Comments", "playSocialSkills", "comment", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Communication")}>
                        {renderBeforeAfterRow("Comprehension (Receptive)", "communicationSkills", "comprehensionBefore", "comprehensionAfter", presentAbsentOptions)}
                        {renderBeforeAfterRow("Expression (Expressive)", "communicationSkills", "expressionBefore", "expressionAfter", presentAbsentOptions)}
                        {renderInput("Comments", "communicationSkills", "comment", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Summaries",
            content: (
                <FormSectionCard title={lx("Summaries")}>
                    {renderInput("Strengths", "summaries", "strengths", null, "textarea")}
                    {renderInput("Areas of Need", "summaries", "needs", null, "textarea")}
                    {renderInput("Overall Behaviour Summary", "summaries", "overallSummary", null, "textarea")}
                </FormSectionCard>
            )
        }
    ];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>{lx("Behavioral Reassessment")}</Typography>
                    <Typography variant="body1" color="text.secondary">{lx("Step")} {step + 1} {lx("of")} {steps.length}: {steps[step].title}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant={lang === 'en' ? 'contained' : 'outlined'} onClick={() => setLang('en')} sx={{ borderRadius: '20px' }}>EN</Button>
                    <Button size="small" variant={lang === 'te' ? 'contained' : 'outlined'} onClick={() => setLang('te')} sx={{ borderRadius: '20px' }}>తెలుగు</Button>
                </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
                <Grid container spacing={1}>
                    {steps.map((s, idx) => (
                        <Grid item xs key={idx}><Box sx={{ height: 4, borderRadius: 2, bgcolor: idx <= step ? 'primary.main' : 'divider', transition: 'all 0.3s' }} /></Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ minHeight: '400px' }}>{steps[step].content}</Box>
            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 8 }}>
                <Button size="small" variant="outlined" startIcon={<ArrowBack />} onClick={() => setStep(step - 1)} disabled={step === 0} sx={{ borderRadius: '12px', px: 3 }}>
                    {lx("Previous Step")}
                </Button>
                {step < steps.length - 1 ? (
                    <Button size="small" variant="contained" endIcon={<ArrowForward />} onClick={() => setStep(step + 1)} sx={{ borderRadius: '12px', px: 4 }}>
                        {lx("Continue")}
                    </Button>
                ) : (
                    <Button
                        disabled={submitting}
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={() => onSubmit(data)}
                        sx={{ borderRadius: '12px', px: 4 }}
                    >
                        {submitting ? lx("Saving...") : lx("Save Reassessment to Profile")}
                    </Button>
                )}
            </Box>
        </Container>
    );
};

export default BehavioralReassessmentForm;
